import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Button, Paper, TextField, FormControlLabel, RadioGroup, Radio, Checkbox, FormLabel, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Table, TableHead, TableRow, TableCell, TableContainer, TableBody, CircularProgress, Alert, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { MobileTimePicker } from '@mui/x-date-pickers/MobileTimePicker';
import moment from 'moment';
import { Iconify } from '../Minimal/src/components/iconify';
import { fetchPromotionsList, fetchPromotionById } from '../../services/PromotionServices';
import '../common/commonStyles.css';
import { API_URL } from '../Config';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const emptyUsers = () => ({
    allCustomers: false,
    salesRep: false,
    pickers: false,
    drivers: false,
});

const USER_ROLE_ID_MAP = {
    allCustomers: 3,
    salesRep: 14,
    pickers: 15,
    drivers: 16,
};

const USER_ROLE_KEY_BY_ID = {
    '3': 'allCustomers',
    '14': 'salesRep',
    '15': 'pickers',
    '16': 'drivers',
};

const parseUsersFromPayload = (raw) => {
    const flags = emptyUsers();
    if (raw == null || raw === '') return flags;
    let keys = [];
    if (Array.isArray(raw)) {
        keys = raw.map(String);
    } else if (typeof raw === 'string') {
        const s = raw.trim();
        try {
            const parsed = JSON.parse(s);
            keys = Array.isArray(parsed) ? parsed.map(String) : [];
        } catch {
            keys = s.split(',').map((x) => x.trim()).filter(Boolean);
        }
    }
    keys.forEach((k) => {
        const normalizedKey = USER_ROLE_KEY_BY_ID[String(k)] || String(k);
        if (normalizedKey in flags) flags[normalizedKey] = true;
    });
    return flags;
};

/** Align time to nearest :00 or :30 so only half-hour slots are used (matches minutesStep={30}). */
const snapScheduledTimeToHalfHour = (m) => {
    if (!m || !moment(m).isValid()) return null;
    const x = moment(m);
    const totalMinutes = x.hours() * 60 + x.minutes();
    const snapped = Math.round(totalMinutes / 30) * 30;
    const h = Math.floor(snapped / 60) % 24;
    const mi = snapped % 60;
    return x.clone().hours(h).minutes(mi).seconds(0).milliseconds(0);
};

export default function CreateNotification({
    pageTitle = 'Create New Notification',
    submitLabel = 'Submit',
    apiPath = 'notifications',
    initialPayload = null,
}) {
    const token = localStorage.getItem('token');
    const navigate = useNavigate();
    const theme = useTheme();
    const promotionDialogFullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const titleLimit = 30;
    const messageLimit = 60;
    const [send, setSend] = useState("now");
    const [notificationTitle, setNotificationTitle] = useState('');
    const [notificationMessage, setNotificationMessage] = useState('');
    const [users, setUsers] = useState(emptyUsers());
    const [notificationMode, setNotificationMode] = useState("promotional");
    const [scheduledDate, setScheduledDate] = useState(null);
    const [scheduledTime, setScheduledTime] = useState();
    const timePickerReferenceDate =
        scheduledDate && moment(scheduledDate).isValid()
            ? moment(scheduledDate).startOf('day').add(12, 'hours')
            : moment().startOf('day').add(12, 'hours');
    const isScheduledDateToday =
        scheduledDate && moment(scheduledDate).isValid()
            ? moment(scheduledDate).isSame(moment(), 'day')
            : false;
    const minScheduledTime = isScheduledDateToday ? moment().startOf('minute') : undefined;
    const timePickerSessionAcceptedRef = useRef(false);
    const [openDatePicker, setOpenDatePicker] = useState(false);
    const [openTimePicker, setOpenTimePicker] = useState(false);
    const [graphicImage, setGraphicImage] = useState(null);
    const [graphicImagePreview, setGraphicImagePreview] = useState('');
    const [graphicImageError, setGraphicImageError] = useState('');
    const [titleLimitReached, setTitleLimitReached] = useState(false);
    const [messageLimitReached, setMessageLimitReached] = useState(false);
    const [promotionDialogOpen, setPromotionDialogOpen] = useState(false);
    const [activePromotions, setActivePromotions] = useState([]);
    const [promotionsLoading, setPromotionsLoading] = useState(false);
    const [promotionsError, setPromotionsError] = useState('');
    const [selectedPromotion, setSelectedPromotion] = useState(null);
    const [tempSelectedPromotionId, setTempSelectedPromotionId] = useState(null);
    const graphicImageInputRef = useRef(null);
    const [validationErrors, setValidationErrors] = useState({
        title: '',
        message: '',
        promotion: '',
        users: '',
        scheduledDate: '',
        scheduledTime: '',
    });
    const [requiredFieldsDialogOpen, setRequiredFieldsDialogOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const handleAudienceChange = (key) => (event) => {
        setUsers((prev) => ({ ...prev, [key]: event.target.checked }));
        setValidationErrors((prev) => ({ ...prev, users: '' }));
    };
    const handleTitleChange = (e) => {
        const nextValue = e.target.value || '';
        setTitleLimitReached(nextValue.length > titleLimit);
        setNotificationTitle(nextValue.slice(0, titleLimit));
        setValidationErrors((prev) => ({ ...prev, title: '' }));
    };
    const handleMessageChange = (e) => {
        const nextValue = e.target.value || '';
        setMessageLimitReached(nextValue.length > messageLimit);
        setNotificationMessage(nextValue.slice(0, messageLimit));
        setValidationErrors((prev) => ({ ...prev, message: '' }));
    };
    const handleGraphicImageChange = (e) => {
        const file = e.target.files?.[0] || null;
        if (!file) {
            return;
        }
        const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
        if (!allowedTypes.includes((file.type || '').toLowerCase())) {
            setGraphicImageError('Only PNG, JPG, JPEG files are allowed');
            if (graphicImageInputRef.current) {
                graphicImageInputRef.current.value = '';
            }
            return;
        }
        const tempUrl = URL.createObjectURL(file);
        if (graphicImagePreview) {
            URL.revokeObjectURL(graphicImagePreview);
        }
        setGraphicImageError('');
        setGraphicImage(file);
        setGraphicImagePreview(tempUrl);
    };
    const handleRemoveGraphicImage = () => {
        if (graphicImagePreview) {
            URL.revokeObjectURL(graphicImagePreview);
        }
        setGraphicImage(null);
        setGraphicImagePreview('');
        setGraphicImageError('');
        if (graphicImageInputRef.current) {
            graphicImageInputRef.current.value = '';
        }
    };
    const loadActivePromotions = async () => {
        setPromotionsLoading(true);
        setPromotionsError('');
        try {
            const response = await fetchPromotionsList({
                status: 'active',
                page: 1,
                pageSize: 100,
                sortBy: 'start_date',
                sortOrder: 'desc',
            });
            const promotions = response?.data?.promotions || [];
            setActivePromotions(Array.isArray(promotions) ? promotions : []);
        } catch (error) {
            setPromotionsError('Failed to load active promotions');
            setActivePromotions([]);
        } finally {
            setPromotionsLoading(false);
        }
    };
    const handleOpenPromotionDialog = async () => {
        setPromotionDialogOpen(true);
        setTempSelectedPromotionId(selectedPromotion?.id || null);
        await loadActivePromotions();
    };
    const handleClosePromotionDialog = () => {
        setPromotionDialogOpen(false);
    };
    const handleConfirmPromotionSelection = () => {
        const picked = activePromotions.find((p) => String(p.id) === String(tempSelectedPromotionId));
        setSelectedPromotion(picked || null);
        setPromotionDialogOpen(false);
        setValidationErrors((prev) => ({ ...prev, promotion: '' }));
    };
    const resolvePromotionById = async (promotionId) => {
        if (!promotionId) return;
        try {
            const response = await fetchPromotionById(promotionId);
            const promotionData = response?.data ?? response?.promotion ?? response;
            if (!promotionData || typeof promotionData !== 'object') return;
            const resolvedTitle = promotionData.title || promotionData.name || '';
            setSelectedPromotion({
                id: promotionData.id ?? promotionId,
                title: resolvedTitle,
                name: resolvedTitle,
            });
            setTempSelectedPromotionId(promotionData.id ?? promotionId);
        } catch {
        }
    };

    useEffect(() => {
        if (!initialPayload || typeof initialPayload !== 'object') return;
        const p = initialPayload;
        const titleVal = String(p.title ?? p.notification_title ?? '');
        const messageVal = String(p.message ?? p.notification_message ?? '');
        setNotificationTitle(titleVal.slice(0, titleLimit));
        setNotificationMessage(messageVal.slice(0, messageLimit));
        setNotificationMode(p.type ?? p.notification_mode ?? 'promotional');
        setUsers(parseUsersFromPayload(p.users ?? p.user_segments ?? p.user_groups ?? []));
        const sendVal =
            String(p.send ?? p.schedule_type ?? '').toLowerCase();
        const isLater =
            sendVal === 'later' ||
            !!(p.scheduled_date || p.schedule_date || p.scheduled_time || p.schedule_time || p.schedule_at);
        setSend(isLater ? 'later' : 'now');
        const d =
            p.scheduled_date ??
            p.schedule_date ??
            p.schedule_at ??
            p.scheduled_datetime;
        const tOnly = p.scheduled_time ?? p.schedule_time;
        let mDate = d ? moment(d) : null;
        let mTime = tOnly ? moment(tOnly, [moment.ISO_8601, 'HH:mm', 'HH:mm:ss'], true) : null;
        if (mDate && !mDate.isValid()) mDate = null;
        if (mTime && !mTime.isValid()) mTime = null;
        setScheduledDate(mDate || null);
        let initialSchedule = null;
        if (mTime) {
            initialSchedule = snapScheduledTimeToHalfHour(mTime);
        } else if (mDate) {
            initialSchedule = snapScheduledTimeToHalfHour(mDate.clone());
        }
        setScheduledTime(initialSchedule);
        const pid =
            p.promotion_id ?? p.promotionId ?? p.promotions_id ?? '';
        const pTitle =
            p.promotion_title ??
            p.promotion_name ??
            (p.promotion && (p.promotion.title || p.promotion.name)) ??
            '';
        if (pid) {
            const pidString = String(pid);
            setSelectedPromotion({
                id: pidString,
                title: pTitle,
                name: pTitle,
            });
            setTempSelectedPromotionId(pidString);
            if (!pTitle) {
                resolvePromotionById(pidString);
            }
        }
        let imgSrc =
            p.image ??
            '';
        if (imgSrc && typeof imgSrc === 'string' && !/^https?:\/\//i.test(imgSrc)) {
            imgSrc = imgSrc.startsWith('/')
                ? `${API_URL.replace(/\/+$/, '')}${imgSrc}`
                : `${API_URL}/${String(imgSrc).replace(/^\/+/, '')}`;
        }
        setGraphicImage(null);
        setGraphicImagePreview(imgSrc ? String(imgSrc) : '');
        setGraphicImageError('');
    }, [initialPayload]);

    const buildValidationErrors = () => {
        const e = {
            title: '',
            message: '',
            promotion: '',
            users: '',
            scheduledDate: '',
            scheduledTime: '',
        };
        if (!String(notificationTitle || '').trim()) e.title = 'Title is required';
        if (!String(notificationMessage || '').trim()) e.message = 'Message is required';
        if (notificationMode === 'promotional' && !selectedPromotion?.id) {
            e.promotion = 'Please select a promotion';
        }
        if (!Object.values(users).some(Boolean)) e.users = 'Select at least one user';
        if (send === 'later') {
            if (!scheduledDate || !moment(scheduledDate).isValid()) e.scheduledDate = 'Date is required';
            if (!scheduledTime || !moment(scheduledTime).isValid()) e.scheduledTime = 'Time is required';
        }
        return e;
    };

    const createNotification = async () => {
        if (submitting) {
            return;
        }
        const err = buildValidationErrors();
        setValidationErrors(err);
        if (Object.values(err).some(Boolean)) {
            setRequiredFieldsDialogOpen(true);
            return;
        }
        setSubmitting(true);
        let navigatedAway = false;
        try {
            const usersArray = Object.keys(users)
                .filter((key) => users[key])
                .map((key) => USER_ROLE_ID_MAP[key])
                .filter((value) => value !== undefined);
            const formData = new FormData();
            formData.append('title', notificationTitle);
            formData.append('message', notificationMessage);
            formData.append('type', notificationMode);
            formData.append('promotion_id', selectedPromotion?.id || null);
            formData.append('users', JSON.stringify(usersArray));
            formData.append('send_mode', send);
            if (send === 'later') {
                formData.append(
                    'scheduled_date',
                    scheduledDate && moment(scheduledDate).isValid()
                        ? moment(scheduledDate).format('YYYY-MM-DD')
                        : ''
                );
                formData.append(
                    'scheduled_time',
                    scheduledTime && moment(scheduledTime).isValid()
                        ? moment(scheduledTime).format('HH:mm')
                        : ''
                );
            }
            formData.append('image', graphicImage || null);
            const response = await fetch(`${API_URL}/${String(apiPath || 'notifications').replace(/^\/+/, '')}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                body: formData,
            });
            const result = await response.json();
            if (result?.success) {
                toast.success(result?.message || 'Notification saved successfully');
                navigatedAway = true;
                navigate('/Notifications/Notification');
            } else {
                toast.error(result?.message || 'Failed to save notification');
            }
        } catch {
            toast.error('Failed to create notification');
        } finally {
            if (!navigatedAway) {
                setSubmitting(false);
            }
        }
    };

    const fieldWidthSx = { width: { xs: '100%', md: '40%' }, maxWidth: '100%' };

    return (
        <Box className="page-container">
            <Paper
                className="paper-style"
                sx={{
                    p: { xs: 1.5, sm: 2 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    gap: { xs: 1, sm: 2 },
                }}
            >
                <Box mb={{ xs: 1, sm: 1.5 }} className="form-input-border">
                    <Typography className="page-title" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>{pageTitle}</Typography>
                </Box>
                <Box mb={1} className="form-input-border" display="flex" flexDirection="column" sx={{ width: '100%' }}>
                    <FormLabel sx={{ fontSize: '14px', fontWeight: 500, color: '#000' }}>Notifications Title <span style={{ color: "red" }}>*</span></FormLabel>
                    <TextField
                        className="form-input-border"
                        size="small"
                        sx={fieldWidthSx}
                        value={notificationTitle}
                        onChange={handleTitleChange}
                        error={!!validationErrors.title || titleLimitReached}
                        helperText={
                            validationErrors.title
                                ? validationErrors.title
                                : titleLimitReached
                                    ? `Maximum ${titleLimit} characters allowed`
                                    : `Upto 30 characters`
                        }
                        inputProps={{ maxLength: titleLimit }}
                        FormHelperTextProps={{
                            sx: {
                                display: 'flex',
                                justifyContent: 'flex-start',
                                textAlign: 'left',
                                mx: 0,
                                marginLeft: 0,
                                marginTop: 0.4,
                                width: '100%',
                                color: '#000',
                            },
                        }}
                    />
                </Box>
                <Box mb={1} className="form-input-border" display="flex" flexDirection="column" sx={{ width: '100%' }}>
                    <FormLabel sx={{ fontSize: '14px', fontWeight: 500, color: '#000' }}>Notifications Message <span style={{ color: "red" }}>*</span></FormLabel>
                    <TextField
                        className="form-input-border"
                        size="small"
                        multiline
                        rows={2}
                        sx={fieldWidthSx}
                        value={notificationMessage}
                        onChange={handleMessageChange}
                        error={!!validationErrors.message || messageLimitReached}
                        helperText={
                            validationErrors.message
                                ? validationErrors.message
                                : messageLimitReached
                                    ? `Maximum ${messageLimit} characters allowed`
                                    : `Upto 60 characters`
                        }
                        inputProps={{ maxLength: messageLimit }}
                        FormHelperTextProps={{
                            sx: {
                                display: 'flex',
                                justifyContent: 'flex-start',
                                textAlign: 'left',
                                mx: 0,
                                marginLeft: 0,
                                marginTop: 0.4,
                                width: '100%',
                                color: '#000',
                            },
                        }}
                    />
                </Box>
                <Box mb={3} className="form-input-border" display="flex" flexDirection="column" sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}>
                        <FormLabel sx={{ fontSize: '14px', fontWeight: 500, color: '#000', m: 0 }}>Graphic Image (Optional)</FormLabel>
                        <Button component="label" variant="outlined" size="small" sx={{ minWidth: { xs: 'unset', sm: 120 }, width: { xs: '100%', sm: 'auto' }, borderColor: '#000', py: 2 }}>
                            Upload Image
                            <input
                                hidden
                                ref={graphicImageInputRef}
                                type="file"
                                accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                                onChange={handleGraphicImageChange}
                            />
                        </Button>

                    </Box>
                    <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#000', mt: 1 }}>Note: Upload a PNG, JPG, or JPEG image with recommended ratio 10:4</Typography>
                    {graphicImageError && (
                        <Typography sx={{ mt: 0.5, width: { xs: '100%', md: '40%' }, maxWidth: '100%', color: 'error.main', fontSize: '0.75rem', textAlign: 'left' }}>
                            {graphicImageError}
                        </Typography>
                    )}
                    {graphicImagePreview ? (
                        <Box sx={{ mt: 1, width: { xs: '100%', md: '40%' }, maxWidth: '100%', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography sx={{ fontSize: '0.875rem', color: '#4B5563', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mr: 1 }}>
                                    {graphicImage?.name || 'Graphic image'}
                                </Typography>
                                <IconButton size="small" onClick={handleRemoveGraphicImage}>
                                    <Iconify icon="eva:close-fill" width={16} />
                                </IconButton>
                            </Box>
                            <Box
                                component="img"
                                src={graphicImagePreview}
                                alt=""
                                sx={{ width: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 1, border: '1px solid #f0f0f0' }}
                            />
                        </Box>
                    ) : null}
                </Box>

                <Box mb={2} sx={{ width: '100%' }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#000' }}>Type of Notification</Typography>
                    <RadioGroup value={notificationMode} defaultValue="promotional" onChange={(e) => {
                        setNotificationMode(e.target.value);
                        setValidationErrors((prev) => ({ ...prev, promotion: '' }));
                    }} sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        flexWrap: 'wrap',
                        gap: { xs: 0.5, sm: 2 },
                        alignItems: 'flex-start',
                    }}>
                        <FormControlLabel value="promotional" control={<Radio className="checkBox" sx={{ color: '#000' }} />} label="Promotional" />
                        <FormControlLabel value="update" control={<Radio className="checkBox" sx={{ color: '#000' }} />} label="Update" />
                        <FormControlLabel value="occasion" control={<Radio className="checkBox" sx={{ color: '#000' }} />} label="Occasion" />
                    </RadioGroup>
                    {notificationMode === "promotional" && (
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: 2, mt: 1 }}>
                            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#000', flexShrink: 0 }}>Choose Promotion <span style={{ color: "red" }}>*</span></Typography>
                            <Button variant="outlined" sx={{ minWidth: { xs: 0, sm: 380 }, width: { xs: '100%', sm: 'auto' }, borderColor: validationErrors.promotion ? 'error.main' : '#000', color: '#000', overflow: 'hidden', textOverflow: 'ellipsis' }} onClick={handleOpenPromotionDialog}>
                                {selectedPromotion?.title || selectedPromotion?.name || 'Pick Promotion'}
                            </Button>
                        </Box>
                    )}
                    {notificationMode === 'promotional' && validationErrors.promotion ? (
                        <Typography sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5, textAlign: 'left', width: '100%' }}>{validationErrors.promotion}</Typography>
                    ) : null}
                </Box>
                <Box mb={2} sx={{ width: '100%' }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#000' }}>Select Users <span style={{ color: "red" }}>*</span></Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: { xs: 1, sm: 2 } }}>
                        <FormControlLabel control={<Checkbox className="checkBox" checked={users.allCustomers} onChange={handleAudienceChange('allCustomers')} />} label="All Customers" />
                        <FormControlLabel control={<Checkbox className="checkBox" checked={users.salesRep} onChange={handleAudienceChange('salesRep')} />} label="Sales Rep." />
                        <FormControlLabel control={<Checkbox className="checkBox" checked={users.pickers} onChange={handleAudienceChange('pickers')} />} label="Pickers" />
                        <FormControlLabel control={<Checkbox className="checkBox" checked={users.drivers} onChange={handleAudienceChange('drivers')} />} label="Drivers" />
                    </Box>
                    {validationErrors.users ? (
                        <Typography sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5, textAlign: 'left', width: '100%' }}>{validationErrors.users}</Typography>
                    ) : null}
                </Box>
                <Box mb={2} display="flex" flexDirection={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'stretch', md: 'flex-start' }} gap={{ xs: 1.5, md: 2 }} sx={{ width: '100%', flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#000', flexShrink: 0, pt: { md: 0.5 } }}>Send</Typography>
                    <RadioGroup name="send" value={send} onChange={(e) => {
                        const v = e.target.value;
                        setSend(v);
                        if (v === 'now') {
                            setValidationErrors((prev) => ({ ...prev, scheduledDate: '', scheduledTime: '' }));
                        }
                    }} sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 2,
                        alignItems: 'flex-start',
                    }}>
                        <FormControlLabel value="now" control={<Radio className="checkBox" sx={{ color: '#000' }} />} label="Now" />
                        <FormControlLabel value="later" control={<Radio className="checkBox" sx={{ color: '#000' }} />} label="Later" />
                    </RadioGroup>
                    {
                        send === "later" && (
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <Box mb={2} sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: { xs: 0, md: 1 }, width: { xs: '100%', md: 'auto' }, minWidth: 0 }}>
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap', flexDirection: { xs: 'column', sm: 'row' }, width: '100%' }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 180 } }}>
                                            <Button
                                                variant="outlined"
                                                sx={{ minWidth: { xs: '100%', sm: 180 }, width: { xs: '100%', sm: 'auto' }, borderColor: validationErrors.scheduledDate ? 'error.main' : '#000', color: '#000' }}
                                                onClick={() => setOpenDatePicker(true)}
                                            >
                                                {scheduledDate ? (
                                                    moment(scheduledDate).format('DD-MMM-YYYY')
                                                ) : (
                                                    <>
                                                        Pick Date <span style={{ color: 'red' }}>*</span>
                                                    </>
                                                )}

                                            </Button>
                                            {validationErrors.scheduledDate ? (
                                                <Typography sx={{ color: 'error.main', fontSize: '0.75rem', textAlign: 'left', alignSelf: 'flex-start', width: '100%' }}>{validationErrors.scheduledDate}</Typography>
                                            ) : null}
                                        </Box>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 180 } }}>
                                            <Button
                                                variant="outlined"
                                                sx={{ minWidth: { xs: '100%', sm: 180 }, width: { xs: '100%', sm: 'auto' }, borderColor: validationErrors.scheduledTime ? 'error.main' : '#000', color: '#000' }}
                                                onClick={() => {
                                                    timePickerSessionAcceptedRef.current = false;
                                                    setOpenTimePicker(true);
                                                }}
                                            >
                                                {scheduledTime != null && moment(scheduledTime).isValid() ? (
                                                    moment(scheduledTime).format('HH:mm')
                                                ) : (
                                                    <>
                                                        Pick Time <span style={{ color: 'red' }}>*</span>
                                                    </>
                                                )}

                                            </Button>
                                            {validationErrors.scheduledTime ? (
                                                <Typography sx={{ color: 'error.main', fontSize: '0.75rem', textAlign: 'left', alignSelf: 'flex-start', width: '100%' }}>{validationErrors.scheduledTime}</Typography>
                                            ) : null}
                                        </Box>
                                    </Box>
                                    <MobileDatePicker
                                        open={openDatePicker}
                                        onClose={() => setOpenDatePicker(false)}
                                        value={scheduledDate}
                                        disablePast
                                        minDate={moment().startOf('day')}
                                        onChange={(value) => {
                                            setScheduledDate(value);
                                            if (
                                                value &&
                                                moment(value).isValid() &&
                                                moment(value).isSame(moment(), 'day') &&
                                                scheduledTime != null &&
                                                moment(scheduledTime).isValid() &&
                                                moment(scheduledTime).isBefore(moment(), 'minute')
                                            ) {
                                                setScheduledTime(null);
                                            }
                                            setOpenDatePicker(false);
                                            setValidationErrors((prev) => ({ ...prev, scheduledDate: '' }));
                                        }}
                                        slotProps={{ textField: { sx: { display: 'none' } } }}
                                    />
                                    <MobileTimePicker
                                        open={openTimePicker}
                                        onClose={() => {
                                            setOpenTimePicker(false);
                                            setTimeout(() => {
                                                if (!timePickerSessionAcceptedRef.current) {
                                                    setScheduledTime(null);
                                                }
                                            }, 0);
                                        }}
                                        value={scheduledTime}
                                        referenceDate={timePickerReferenceDate}
                                        minTime={minScheduledTime}
                                        ampm={false}
                                        minutesStep={30}
                                        onAccept={(value) => {
                                            timePickerSessionAcceptedRef.current = true;
                                            const raw =
                                                value != null && moment(value).isValid()
                                                    ? moment(value)
                                                    : null;
                                            const next = raw ? snapScheduledTimeToHalfHour(raw) : null;
                                            setScheduledTime(next);
                                            setOpenTimePicker(false);
                                            setValidationErrors((prev) => ({ ...prev, scheduledTime: '' }));
                                        }}
                                        slotProps={{ textField: { sx: { display: 'none' } } }}
                                    />
                                </Box>
                            </LocalizationProvider>
                        )
                    }
                </Box>

                <Box borderTop={1} borderColor="#0000002f" pt={1} sx={{ width: '100%' }}>
                    <Button variant="contained" onClick={createNotification} disabled={submitting} className="button-variant-contained" sx={{ width: { xs: '100%', sm: 'auto' }, py: { xs: 1.25, sm: 1 }, minWidth: submitting ? 140 : undefined }}>
                        {submitting ? (
                            <CircularProgress color="inherit" size={22} />
                        ) : (
                            submitLabel
                        )}
                    </Button>
                </Box>
            </Paper>
            <Dialog open={promotionDialogOpen} onClose={handleClosePromotionDialog} maxWidth="md" fullWidth fullScreen={promotionDialogFullScreen} scroll="paper">
                <DialogTitle>Select Active Promotion</DialogTitle>
                <DialogContent>
                    {promotionsError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {promotionsError}
                        </Alert>
                    )}
                    {promotionsLoading ? (
                        <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : (
                        <TableContainer sx={{ overflowX: 'auto', maxWidth: '100%', WebkitOverflowScrolling: 'touch' }}>
                            <Table size="small" className="table-cell-border" sx={{ minWidth: { xs: 520, sm: 'auto' } }}>
                                <TableHead>
                                    <TableRow className="table-cell-header">
                                        <TableCell sx={{ width: 70 }}>Select</TableCell>
                                        <TableCell sx={{ width: 80 }}>Id</TableCell>
                                        <TableCell sx={{ width: 230 }}>Title</TableCell>
                                        <TableCell sx={{ width: 170 }}>Type</TableCell>

                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {activePromotions.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">No active promotions found</TableCell>
                                        </TableRow>
                                    ) : (
                                        activePromotions.map((promotion) => (
                                            <TableRow key={promotion.id} hover>
                                                <TableCell>
                                                    <Checkbox
                                                        className="checkBox"
                                                        checked={String(tempSelectedPromotionId) === String(promotion.id)}
                                                        onChange={() => setTempSelectedPromotionId(
                                                            String(tempSelectedPromotionId) === String(promotion.id) ? null : promotion.id
                                                        )}
                                                    />
                                                </TableCell>
                                                <TableCell>{promotion.id}</TableCell>
                                                <TableCell>{promotion.title || promotion.name || '-'}</TableCell>
                                                <TableCell>{promotion.type || promotion.promotion_type || '-'}</TableCell>

                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions sx={{ flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: 1, px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 1 }, '& > button': { width: { xs: '100%', sm: 'auto' } } }}>
                    <Button variant="outlined" onClick={handleClosePromotionDialog}>Cancel</Button>
                    <Button variant="contained" className="button-variant-contained" onClick={handleConfirmPromotionSelection} disabled={!tempSelectedPromotionId}>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={requiredFieldsDialogOpen}
                onClose={() => setRequiredFieldsDialogOpen(false)}
                maxWidth="sm"
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
          
                <DialogContent sx={{ p: 2 }}>
                    <Typography variant="body2" color="#000" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        Please fill the required fields
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }} justifyContent="end">
                    <Button
                        variant="contained"
                        className="button-variant-contained"
                        onClick={() => setRequiredFieldsDialogOpen(false)}
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}